import {ContentContainer} from '@coolcolduk/ui-mui';
import {ContentCopy, Refresh} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Typography,
} from '@mui/material';
import {useState} from 'react';
import {env} from '../../../config/env';
import useSecrets from '../../../hooks/useSecrets';

interface BuildInfo {
  buildTimestamp: string;
  commitHash: string;
  branch: string;
  version: string;
  environment: string;
  nodeVersion: string;
  buildNumber?: string;
  deployUrl?: string;
  lastDeploy?: string;
}

export default function AdminInfoPage() {
  const [buildInfo] = useState<BuildInfo>(env.info);
  const {secrets, loading: secretsLoading, error: secretsError, refetch: refetchSecrets} = useSecrets();

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  const handleRefresh = () => {
    // In a real app, this might fetch fresh build info from an API
    window.location.reload();
  };

  const handleRefreshSecrets = () => {
    refetchSecrets();
  };

  const buildInfoItems = [
    {label: 'Build Timestamp', value: buildInfo.buildTimestamp, sensitive: false},
    {label: 'Commit Hash', value: buildInfo.commitHash, sensitive: true},
    {label: 'Branch', value: buildInfo.branch, sensitive: false},
    {label: 'Version', value: buildInfo.version, sensitive: false},
    {label: 'Environment', value: buildInfo.environment, sensitive: false},
    {label: 'Node Version', value: buildInfo.nodeVersion, sensitive: false},
    {label: 'Build Number', value: buildInfo.buildNumber || 'Not set', sensitive: false},
    {label: 'Deploy URL', value: buildInfo.deployUrl || 'Not set', sensitive: true},
    {label: 'Last Deploy', value: buildInfo.lastDeploy || 'Not available', sensitive: false},
  ];

  return (
    <ContentContainer data-testid="info-management-page">
      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3}}>
        <Typography variant="h5" component="h2">
          Build Information
        </Typography>
        <Button variant="outlined" startIcon={<Refresh />} onClick={handleRefresh} sx={{textTransform: 'none'}}>
          Refresh
        </Button>
      </Box>

      <Alert severity="info" sx={{mb: 3}}>
        This page displays sensitive build and deployment information. Only administrators should have access to this
        data.
      </Alert>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            System Information
          </Typography>
          <Divider sx={{mb: 2}} />

          {buildInfoItems.map((item) => (
            <Box key={item.label} sx={{mb: 2}}>
              <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                <Typography variant="body2" color="text.secondary" sx={{minWidth: 120}}>
                  {item.label}:
                </Typography>
                <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flex: 1}}>
                  <Typography
                    variant="body2"
                    sx={{
                      fontFamily: 'monospace',
                      backgroundColor: item.sensitive ? 'error.light' : 'grey.100',
                      px: 1,
                      py: 0.5,
                      borderRadius: 1,
                      flex: 1,
                      wordBreak: 'break-all',
                    }}
                  >
                    {item.value}
                  </Typography>
                  <IconButton size="small" onClick={() => handleCopyToClipboard(item.value)} sx={{ml: 1}}>
                    <ContentCopy fontSize="small" />
                  </IconButton>
                  {item.sensitive && <Chip label="Sensitive" size="small" color="error" variant="outlined" />}
                </Box>
              </Box>
            </Box>
          ))}
        </CardContent>
      </Card>

      <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, mt: 4}}>
        <Typography variant="h5" component="h2">
          Secrets
        </Typography>
        <Button
          variant="outlined"
          startIcon={secretsLoading ? <CircularProgress size={16} /> : <Refresh />}
          onClick={handleRefreshSecrets}
          disabled={secretsLoading}
          sx={{textTransform: 'none'}}
        >
          Refresh
        </Button>
      </Box>

      <Alert severity="warning" sx={{mb: 3}}>
        This section displays masked secrets in use. Only administrators should have access to this data. Secrets are
        masked for security purposes.
      </Alert>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Environment Secrets
          </Typography>
          <Divider sx={{mb: 2}} />

          {secretsLoading && (
            <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
              <CircularProgress />
            </Box>
          )}

          {secretsError && (
            <Alert severity="error" sx={{mb: 2}}>
              Failed to load secrets: {secretsError.message}
            </Alert>
          )}

          {!secretsLoading && !secretsError && secrets.length === 0 && (
            <Alert severity="info" sx={{mb: 2}}>
              No secrets found.
            </Alert>
          )}

          {!secretsLoading &&
            !secretsError &&
            secrets.map((secret) => (
              <Box key={`InfoManagementPage-Box-${secret.name}`} sx={{mb: 2}}>
                <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                  <Typography variant="body2" color="text.secondary" sx={{minWidth: 120}}>
                    {secret.name}:
                  </Typography>
                  <Box sx={{display: 'flex', alignItems: 'center', gap: 1, flex: 1}}>
                    <Typography
                      variant="body2"
                      sx={{
                        fontFamily: 'monospace',
                        backgroundColor: 'warning.light',
                        px: 1,
                        py: 0.5,
                        borderRadius: 1,
                        flex: 1,
                        wordBreak: 'break-all',
                      }}
                    >
                      {secret.maskedValue}
                    </Typography>
                    <IconButton size="small" onClick={() => handleCopyToClipboard(secret.maskedValue)} sx={{ml: 1}}>
                      <ContentCopy fontSize="small" />
                    </IconButton>
                    <Chip label="Secret" size="small" color="warning" variant="outlined" />
                  </Box>
                </Box>
              </Box>
            ))}
        </CardContent>
      </Card>
    </ContentContainer>
  );
}
